import React from 'react';

import { Modal } from '@buildoutinc/blueprint-react/ui/Modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlugCircleBolt } from '@fortawesome/pro-regular-svg-icons';

import { Button } from 'components/ui/button';
import ContinueWithGoogleButton from 'components/nylas/v3/grant/continue_with_google_button';

import { buildReauthUrl, MAILBOX_LABELS } from './helpers';

import type { ProviderName } from 'components/nylas/v3/types';

interface Props {
  connected: boolean;
  expired?: boolean;
  providerName?: ProviderName;
  authPath: string;
  contactId: number;
  onClose: () => void;
}

function reauthMessage(reconnectProvider: ProviderName | undefined, expired: boolean | undefined): string {
  if (!reconnectProvider) {
    return 'Connect your email account to send from here. You\'ll grant send permission once and come ' +
      'right back to finish your email.';
  }

  const mailbox = MAILBOX_LABELS[reconnectProvider];
  if (expired) {
    return `Your ${mailbox} connection has expired. Reconnect it to send — you'll come right back here to finish ` +
      'your email.';
  }

  return `Sending email needs a permission your ${mailbox} connection doesn't have yet. Reconnect it to grant send ` +
    'access — you\'ll come right back here to finish your email.';
}

export default function ReauthModal({ connected, expired, providerName, authPath, contactId, onClose }: Props) {
  const reconnectProvider = connected ? providerName : undefined;

  const startReauth = (provider: ProviderName) => {
    window.location.assign(buildReauthUrl(authPath, provider, contactId).toString());
  };

  return (
    <Modal open onClose={onClose}>
      <Modal.Content>
        <Modal.Header>
          <Modal.Title className="d-flex gap-2 align-items-center">
            <FontAwesomeIcon className="text-primary" icon={faPlugCircleBolt} />
            {reconnectProvider ? 'Reconnect your email to send' : 'Connect your email to send'}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <p className="mb-0">{reauthMessage(reconnectProvider, expired)}</p>
        </Modal.Body>

        <Modal.Footer>
          <Button label="Cancel" variant="ghost" onClick={onClose} />
          {reconnectProvider === 'google' && <ContinueWithGoogleButton onClick={() => startReauth('google')} />}
          {reconnectProvider === 'microsoft' && (
            <Button onClick={() => startReauth('microsoft')}>Reconnect {MAILBOX_LABELS.microsoft}</Button>
          )}
          {!reconnectProvider && (
            <>
              <ContinueWithGoogleButton onClick={() => startReauth('google')} />
              <Button variant="outline" onClick={() => startReauth('microsoft')}>
                Sign in with {MAILBOX_LABELS.microsoft}
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
}
